// Import react elements
import { useRef, useEffect, useState, useCallback } from 'react';

// Import utility functions
import { SanitizeHtml } from "global/utilities/SanitizeHTML";

// Import css
import './RichTextInput.css';



// Flexible rich text input component
const RichTextInput = ({ value, onChange, disabled = false, placeholder = "Enter text..." }) => {


    // Ref to access the editable div directly
    const editorRef = useRef(null);

    // Ref to store the last known HTML value so we can avoid unnecessary re-renders
    const lastHtmlRef = useRef('');

    // Track active formatting states for the toolbar buttons
    const [activeFormats, setActiveFormats] = useState({

        // Track whether bold is currently active at the cursor / selection
        bold: false,

        // Track whether italic is currently active at the cursor / selection
        italic: false,

        // Track whether underline is currently active at the cursor / selection
        underline: false,

    });

            

    // useEffect hook to set the editor's innerHTML only when the `value` prop changes externally
    useEffect(() => {

        // Check the editor exists and the incoming value is different from what we last rendered
        if (editorRef.current && value !== lastHtmlRef.current) {

            // Set the content of the contentEditable element (the input area)
            editorRef.current.innerHTML = value || '';

            // Update our reference to track the last rendered value
            lastHtmlRef.current = value;

        }

        // This effect runs only when the input value changes
    }, [value]);



    // Update toolbar active state based on current selection/caret position
    const updateActiveFormats = useCallback(() => {

        // If disabled, force all toolbar states off
        if (disabled) {

            // Reset toolbar state
            setActiveFormats({

                // Bold off
                bold: false,

                // Italic off
                italic: false,

                // Underline off
                underline: false,

            });

            // Stop here
            return;

        }

        // If there is no editor yet, stop
        if (!editorRef.current) {

            // Exit early
            return;

        }

        // Get the current selection object
        const selection = window.getSelection();

        // If there is no selection, stop
        if (!selection || selection.rangeCount === 0) {

            // Exit early
            return;

        }

        // Get the node where the caret/selection starts
        const anchorNode = selection.anchorNode;

        // Check whether the current selection is inside this editor
        const isInsideEditor = anchorNode && editorRef.current.contains(anchorNode);

        // If selection is outside this editor, do not highlight anything
        if (!isInsideEditor) {

            // Reset toolbar state
            setActiveFormats({

                // Bold off
                bold: false,

                // Italic off
                italic: false,

                // Underline off
                underline: false,

            });

            // Stop here
            return;

        }

        // Read browser command states for the current selection
        setActiveFormats({

            // Bold state
            bold: document.queryCommandState('bold'),

            // Italic state
            italic: document.queryCommandState('italic'),

            // Underline state
            underline: document.queryCommandState('underline'),

        });

    }, [disabled]);



    // Check whether a text node only contains editor caret placeholders
    const isCaretPlaceholderNode = (node) => {

        // Return false when the node is not a text node
        if (!node || node.nodeType !== Node.TEXT_NODE) {

            // The node is not a placeholder text node
            return false;

        }

        // Remove zero-width characters and check whether anything remains
        return node.textContent.replace(/\u200B/g, '').trim() === '';

    };



    // Find the node immediately before the current caret position
    const getNodeBeforeCaret = (range) => {

        // Get the node containing the caret
        const startContainer = range.startContainer;

        // Get the caret offset inside the node
        const startOffset = range.startOffset;

        // Handle a caret inside a text node
        if (startContainer.nodeType === Node.TEXT_NODE) {

            // Get the text before the caret
            const textBeforeCaret = startContainer.textContent.slice(0, startOffset);

            // Remove zero-width caret placeholders
            const visibleTextBeforeCaret = textBeforeCaret.replace(/\u200B/g, '');

            // The caret has visible text immediately before it
            if (visibleTextBeforeCaret.length > 0) {

                // This is not a second consecutive line break
                return null;

            }

            // Start with the previous sibling
            let previousNode = startContainer.previousSibling;

            // Skip empty placeholder text nodes
            while (previousNode && isCaretPlaceholderNode(previousNode)) {

                // Move to the previous sibling
                previousNode = previousNode.previousSibling;

            }

            // Return the previous meaningful node
            return previousNode;

        }

        // Get the child immediately before the caret
        let previousNode = startContainer.childNodes[startOffset - 1] || null;

        // Skip empty placeholder text nodes
        while (previousNode && isCaretPlaceholderNode(previousNode)) {

            // Move to the previous sibling
            previousNode = previousNode.previousSibling;

        }

        // Return the previous meaningful node
        return previousNode;

    };



    // Move the caret into a newly-created paragraph
    const placeCaretInsideParagraph = (paragraphElement) => {

        // Create a line break so the empty paragraph remains visible
        const paragraphBreak = document.createElement('br');

        // Add the line break to the paragraph
        paragraphElement.appendChild(paragraphBreak);

        // Create a zero-width text node for reliable caret placement
        const caretPlaceholder = document.createTextNode('\u200B');

        // Add the caret placeholder after the line break
        paragraphElement.appendChild(caretPlaceholder);

        // Create a new selection range
        const nextRange = document.createRange();

        // Place the range after the zero-width character
        nextRange.setStart(caretPlaceholder, 1);

        // Collapse the range into a caret
        nextRange.collapse(true);

        // Get the browser selection
        const selection = window.getSelection();

        // Remove the previous selection
        selection.removeAllRanges();

        // Apply the new caret position
        selection.addRange(nextRange);

    };



    // Insert a single line break at the current caret position
    const insertLineBreak = (range) => {

        // Delete any currently selected content
        range.deleteContents();

        // Create the line break
        const lineBreak = document.createElement('br');

        // Insert the line break at the caret
        range.insertNode(lineBreak);

        // Create a zero-width character for reliable caret placement
        const caretPlaceholder = document.createTextNode('\u200B');

        // Insert the caret placeholder after the line break
        lineBreak.parentNode.insertBefore(
            caretPlaceholder,
            lineBreak.nextSibling,
        );

        // Create a new caret range
        const nextRange = document.createRange();

        // Place the caret after the zero-width character
        nextRange.setStart(caretPlaceholder, 1);

        // Collapse the range into a caret
        nextRange.collapse(true);

        // Get the browser selection
        const selection = window.getSelection();

        // Remove the previous selection
        selection.removeAllRanges();

        // Apply the new caret position
        selection.addRange(nextRange);

    };



    // Replace a second consecutive line break with a new paragraph
    const insertParagraph = (range, previousLineBreak) => {

        // Get the element containing the caret
        const caretElement = range.startContainer.nodeType === Node.ELEMENT_NODE
            ? range.startContainer
            : range.startContainer.parentElement;

        // Find the paragraph currently containing the caret
        const currentParagraph = caretElement?.closest('p');

        // Store the parent containing the previous line break
        const lineBreakParent = previousLineBreak.parentNode;

        // Store the line break position before removing it
        const lineBreakIndex = Array.from(lineBreakParent.childNodes).indexOf(previousLineBreak);

        // Check whether the current caret node is only a placeholder
        if (isCaretPlaceholderNode(range.startContainer)) {

            // Remove the old caret placeholder
            range.startContainer.remove();

        }

        // Remove the previous line break
        previousLineBreak.remove();

        // Create the new paragraph
        const nextParagraph = document.createElement('p');

        // Insert after the current paragraph when one exists
        if (currentParagraph && currentParagraph.parentNode) {

            // Insert the new paragraph after the current paragraph
            currentParagraph.parentNode.insertBefore(
                nextParagraph,
                currentParagraph.nextSibling,
            );

        } else {

            // Create a new insertion range
            const paragraphRange = document.createRange();

            // Restore the position where the removed line break existed
            paragraphRange.setStart(
                lineBreakParent,
                Math.min(lineBreakIndex, lineBreakParent.childNodes.length),
            );

            // Collapse the range into an insertion point
            paragraphRange.collapse(true);

            // Insert the paragraph
            paragraphRange.insertNode(nextParagraph);

        }

        // Move the caret into the new paragraph
        placeCaretInsideParagraph(nextParagraph);

    };



    // Handle line breaks and paragraph creation
    const handleKeyDown = (event) => {

        // Ignore keys other than Enter
        if (event.key !== 'Enter') {

            // Allow the browser to handle the key normally
            return;

        }

        // Stop editing when the editor is disabled
        if (disabled) {

            // Prevent browser changes
            event.preventDefault();

            // Stop here
            return;

        }

        // Get the current browser selection
        const selection = window.getSelection();

        // Stop when there is no valid selection
        if (!selection || selection.rangeCount === 0) {

            // Stop here
            return;

        }

        // Get the current selection range
        const range = selection.getRangeAt(0);

        // Check whether the selection is inside this editor
        const isInsideEditor = editorRef.current?.contains(range.startContainer);

        // Allow normal behaviour when the selection is outside this editor
        if (!isInsideEditor) {

            // Stop here
            return;

        }

        // Prevent the browser from creating its own div or paragraph
        event.preventDefault();

        // Find the previous meaningful node
        const previousNode = getNodeBeforeCaret(range);

        // Check whether this is the second consecutive Enter press
        const isSecondLineBreak = previousNode?.nodeName === 'BR';

        // Create a new paragraph after two consecutive Enter presses
        if (isSecondLineBreak) {

            // Replace the previous break with a paragraph boundary
            insertParagraph(range, previousNode);

        } else {

            // Insert a single line break
            insertLineBreak(range);

        }

        // Synchronise the new HTML with the parent component
        handleInput();

        // Refresh the toolbar formatting states
        updateActiveFormats();

    };



    // Handle content changes inside the editable div
    const handleInput = () => {

        // Stop editing logic if the editor is disabled
        if (disabled) {

            // Exit early
            return;

        }

        // Get raw HTML from the editable div
        const rawHtml = editorRef.current.innerHTML;

        // Remove temporary caret placeholder characters
        const htmlWithoutCaretPlaceholders = rawHtml.replace(/\u200B/g, '');

        // Sanitize the HTML to remove disallowed tags
        const cleanHtml = SanitizeHtml(htmlWithoutCaretPlaceholders);

        // Avoid an infinite loop when nothing has changed
        if (cleanHtml !== lastHtmlRef.current) {

            // Store the clean HTML
            lastHtmlRef.current = cleanHtml;

            // Pass the sanitized HTML back to the parent
            onChange(cleanHtml);

        }

        // Refresh the toolbar state after input
        updateActiveFormats();

    };

    

    // Apply rich-text formatting using `document.execCommand`
    // Note: execCommand is deprecated but still widely supported for basic formatting
    const applyFormat = (cmd, arg = null) => {

        // Stop if disabled
        if (disabled) {

            // Exit early
            return;

        }

        // Ensure the editor keeps focus before applying formatting
        editorRef.current?.focus();

        // Run the command (e.g., bold, italic, underline, etc.)
        document.execCommand(cmd, false, arg);

        // Sync the updated HTML back to state
        handleInput();

        // Refresh toolbar active state after formatting
        updateActiveFormats();
        
    };



    // Listen for selection changes so toolbar updates when user moves cursor
    useEffect(() => {

        // Handler for browser selection changes
        const handleSelectionChange = () => {

            // Update toolbar active states
            updateActiveFormats();

        };

        // Add document-level selection listener
        document.addEventListener('selectionchange', handleSelectionChange);

        // Cleanup listener on unmount
        return () => {

            // Remove document-level selection listener
            document.removeEventListener('selectionchange', handleSelectionChange);

        };

    }, [updateActiveFormats]);



    return (

        <div className="rich-input">

            {/* Toolbar */}
            <div className="rich-input__toolbar">

                <button
                    type="button"
                    className={`rich-input__toolbar-button ${activeFormats.bold ? "rich-input__toolbar-button--active" : ""}`}
                    onMouseDown={(event) => {

                        // Prevent button click from stealing selection/focus from the editor
                        event.preventDefault();

                    }}
                    onClick={(event) => {

                        // Stop the click from bubbling to the popup/form
                        event.stopPropagation();

                        // Apply bold formatting
                        applyFormat('bold');

                    }}
                    disabled={disabled}
                >
                    <b>B</b>
                </button>

                <button
                    type="button"
                    className={`rich-input__toolbar-button ${activeFormats.italic ? "rich-input__toolbar-button--active" : ""}`}
                    onMouseDown={(event) => {

                        // Prevent button click from stealing selection/focus from the editor
                        event.preventDefault();

                    }}
                    onClick={(event) => {

                        // Stop the click from bubbling to the popup/form
                        event.stopPropagation();

                        // Apply italic formatting
                        applyFormat('italic');

                    }}
                    disabled={disabled}
                >
                    <i>I</i>
                </button>

                <button
                    type="button"
                    className={`rich-input__toolbar-button ${activeFormats.underline ? "rich-input__toolbar-button--active" : ""}`}
                    onMouseDown={(event) => {

                        // Prevent button click from stealing selection/focus from the editor
                        event.preventDefault();

                    }}
                    onClick={(event) => {

                        // Stop the click from bubbling to the popup/form
                        event.stopPropagation();

                        // Apply underline formatting
                        applyFormat('underline');

                    }}
                    disabled={disabled}
                >
                    <u>U</u>
                </button>

                {/* <button type="button" onClick={() => applyFormat('formatBlock', 'blockquote')}>"</button> */}

            </div>

            {/* Editable area */}
            <div

                // Custom CSS class for styling
                className={`rich-input__editor ${disabled ? "rich-input__editor--disabled" : ""}`}

                // Link the ref to access the DOM node
                ref={editorRef}

                // Make the div editable like an input
                contentEditable={!disabled}

                // Prevent React warning for contentEditable managed content
                suppressContentEditableWarning={true}

                // Store placeholder for CSS pseudo-element
                data-placeholder={placeholder}

                // Handle changes as user types
                onInput={handleInput}
                
                // Override the browser Enter key behaviour
                onKeyDown={handleKeyDown}

                // Update toolbar when user clicks in editor
                onMouseUp={updateActiveFormats}

                // Update toolbar when user navigates with keyboard
                onKeyUp={updateActiveFormats}

                // Update toolbar when editor receives focus
                onFocus={updateActiveFormats}

            />

        </div>
    );
};

export default RichTextInput;